/**
 * Judge.me Reviews Section
 * Custom integration with Judge.me API and Swiper.js
 */

class JudgeReviews {
  constructor(section) {
    this.section = section;

    // Hardcoded credentials for testing
    this.publicToken = '6_rcUxNh75ypUNP-e7DqXhaU6HA';
    this.privateToken = 'DmhHl2pqf7YFvb0W9W2H1XKTI7c';
    this.shopDomain = 'sova-care.myshopify.com';
    this.reviewsPerPage = 10;

    // Get product data from section attributes
    this.productId = section.dataset.productId || null;
    this.productHandle = section.dataset.productHandle || null;
    this.productRating = section.dataset.productRating ? parseFloat(section.dataset.productRating) : null;
    this.productRatingCount = section.dataset.productRatingCount ? parseInt(section.dataset.productRatingCount) : null;

    this.carousel = section.querySelector('.judge-reviews__carousel');
    this.wrapper = section.querySelector('.swiper-wrapper');
    this.loading = section.querySelector('.judge-reviews__loading');
    this.badge = section.querySelector('.judge-reviews__badge');

    this.swiper = null;

    // Debug info
    console.log('Judge.me Reviews - Configuration:', {
      publicToken: this.publicToken.substring(0, 10) + '...',
      shopDomain: this.shopDomain,
      reviewsPerPage: this.reviewsPerPage,
      productId: this.productId,
      productHandle: this.productHandle,
      productRating: this.productRating,
      productRatingCount: this.productRatingCount
    });

    this.init();
  }

  async init() {
    try {
      const data = await this.fetchReviews();

      if (data && data.reviews && data.reviews.length > 0) {
        this.updateBadge(data);
        this.renderReviews(data.reviews);
        this.initSwiper();
      } else if (data && data.error) {
        console.warn('Judge.me API error:', data.error);
        this.showApiError(data.error);
      } else {
        this.showNoReviews();
      }

      this.hideLoading();
    } catch (error) {
      console.error('Error loading Judge.me reviews:', error);
      this.showApiError(error.message);
      this.hideLoading();
    }
  }

  /**
   * Fetch reviews from Judge.me Widget API (Public Token)
   * If productId is available, fetch product-specific reviews
   * Otherwise, fetch all shop reviews
   */
  async fetchReviews() {
    let url;

    // Fetch product-specific reviews if productId is available
    if (this.productId) {
      url = `https://judge.me/api/v1/widgets/product_review?api_token=${this.publicToken}&shop_domain=${this.shopDomain}&external_id=${this.productId}&per_page=${this.reviewsPerPage}`;
      console.log('Judge.me Product Review API Request:', url);
    } else {
      // Fetch all reviews from the shop
      url = `https://judge.me/api/v1/widgets/all_reviews_page?api_token=${this.publicToken}&shop_domain=${this.shopDomain}&per_page=${this.reviewsPerPage}`;
      console.log('Judge.me All Reviews API Request:', url);
    }

    try {
      const response = await fetch(url);
      const data = await response.json();

      console.log('Judge.me Widget API Response:', data);

      // Check for different response structures
      const widgetHTML = data.widget || data.all_reviews;

      if (widgetHTML) {
        // Parse HTML widget to extract review data
        const reviews = this.parseReviewsFromHTML(widgetHTML);
        console.log(`✅ Successfully parsed ${reviews.length} reviews from widget HTML`);

        return { reviews };
      } else {
        throw new Error('No reviews data in response');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      throw error;
    }
  }

  /**
   * Parse reviews from Judge.me HTML widget
   */
  parseReviewsFromHTML(html) {
    // Create a temporary DOM element to parse HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const reviewElements = doc.querySelectorAll('.jdgm-rev');
    const reviews = [];

    reviewElements.forEach((el) => {
      try {
        // Extract rating
        const ratingEl = el.querySelector('.jdgm-rev__rating');
        const rating = ratingEl ? parseInt(ratingEl.dataset.score) : 0;

        // Extract reviewer name
        const authorEl = el.querySelector('.jdgm-rev__author');
        const name = authorEl ? authorEl.textContent.trim() : 'Anonymous';

        // Extract review title
        const titleEl = el.querySelector('.jdgm-rev__title');
        const title = titleEl ? titleEl.textContent.trim() : '';

        // Extract review body
        const bodyEl = el.querySelector('.jdgm-rev__body');
        const body = bodyEl ? bodyEl.textContent.trim() : '';

        // Extract date
        const timestampEl = el.querySelector('.jdgm-rev__timestamp');
        const created_at = timestampEl ? timestampEl.getAttribute('data-content') : new Date().toISOString();

        // Extract verified buyer status
        const verified = el.getAttribute('data-verified-buyer') === 'true' ? 'verified-purchase' : 'unconfirmed-buyer';

        // Extract pictures if any
        const pictureEls = el.querySelectorAll('.jdgm-rev-carousel__item img');
        const pictures = Array.from(pictureEls).map(img => ({
          urls: {
            small: img.src,
            original: img.src
          }
        }));

        reviews.push({
          rating,
          title,
          body,
          verified,
          created_at,
          pictures: pictures.length > 0 ? pictures : [],
          reviewer: {
            name
          }
        });
      } catch (err) {
        console.warn('Error parsing review element:', err);
      }
    });

    return reviews;
  }

  /**
   * Update rating badge with average rating and review count
   * Uses product metafields if available, otherwise calculates from reviews
   */
  updateBadge(data) {
    const ratingValue = this.section.querySelector('.judge-reviews__rating-value');
    const ratingLink = this.section.querySelector('.judge-reviews__rating-link');
    const starsContainer = this.section.querySelector('.judge-reviews__stars');

    // Prioritize product metafields, fallback to calculated values
    let averageRating = this.productRating || 0;
    let reviewCount = this.productRatingCount || 0;

    // If metafields not available, calculate from reviews
    if (!averageRating && data.reviews && data.reviews.length > 0) {
      const sum = data.reviews.reduce((acc, review) => acc + review.rating, 0);
      averageRating = sum / data.reviews.length;
    }

    if (!reviewCount && data.reviews) {
      reviewCount = data.reviews.length;
    }

    // Update rating value
    if (ratingValue && averageRating > 0) {
      ratingValue.textContent = averageRating.toFixed(1);
    }

    // Update review count link
    if (ratingLink) {
      if (reviewCount > 0) {
        ratingLink.textContent = `${reviewCount} avis`;
      } else {
        ratingLink.textContent = '-- avis';
      }
    }

    // Update stars
    if (starsContainer && averageRating > 0) {
      starsContainer.innerHTML = this.generateStars(averageRating, 'large');
      starsContainer.dataset.rating = averageRating;
    }
  }

  /**
   * Render all review cards
   */
  renderReviews(reviews) {
    if (!this.wrapper) return;

    this.wrapper.innerHTML = '';

    reviews.forEach(review => {
      const card = this.createReviewCard(review);
      const slide = document.createElement('div');
      slide.className = 'swiper-slide';
      slide.appendChild(card);
      this.wrapper.appendChild(slide);
    });
  }

  /**
   * Create individual review card
   */
  createReviewCard(review) {
    const card = document.createElement('div');
    card.className = 'judge-reviews__card';

    // Card Header
    const header = document.createElement('div');
    header.className = 'judge-reviews__card-header';

    const user = document.createElement('div');
    user.className = 'judge-reviews__card-user';

    // Avatar
    const avatar = document.createElement('div');
    avatar.className = 'judge-reviews__avatar';
    const avatarText = document.createElement('p');
    avatarText.className = 'judge-reviews__avatar-text';
    avatarText.textContent = this.getInitials(review.reviewer.name || 'Anonymous');
    avatar.appendChild(avatarText);

    // User Info
    const userInfo = document.createElement('div');
    userInfo.className = 'judge-reviews__user-info';

    const userHeader = document.createElement('div');
    userHeader.className = 'judge-reviews__user-header';

    const userName = document.createElement('p');
    userName.className = 'judge-reviews__user-name';
    userName.textContent = review.reviewer.name || 'Anonymous';

    const rating = document.createElement('div');
    rating.className = 'judge-reviews__card-rating';
    rating.innerHTML = this.generateStars(review.rating, 'small');

    userHeader.appendChild(userName);
    userHeader.appendChild(rating);

    const date = document.createElement('p');
    date.className = 'judge-reviews__date';
    date.textContent = this.formatDate(review.created_at);

    userInfo.appendChild(userHeader);
    userInfo.appendChild(date);

    user.appendChild(avatar);
    user.appendChild(userInfo);
    header.appendChild(user);

    // Card Body
    const body = document.createElement('div');
    body.className = 'judge-reviews__card-body';

    const content = document.createElement('div');
    content.className = 'judge-reviews__card-content';

    // Review Title
    if (review.title) {
      const title = document.createElement('h3');
      title.className = 'judge-reviews__review-title';
      title.textContent = review.title;
      content.appendChild(title);
    }

    // Review Content
    if (review.body) {
      const text = document.createElement('p');
      text.className = 'judge-reviews__review-content';
      text.textContent = review.body;
      content.appendChild(text);
    }

    body.appendChild(content);

    // Review Images
    if (review.pictures && review.pictures.length > 0) {
      const images = document.createElement('div');
      images.className = 'judge-reviews__images';

      review.pictures.slice(0, 2).forEach(picture => {
        const img = document.createElement('img');
        img.className = 'judge-reviews__image';
        img.src = picture.urls.small || picture.urls.original;
        img.alt = 'Photo du client';
        img.loading = 'lazy';
        images.appendChild(img);
      });

      body.appendChild(images);
    }

    card.appendChild(header);
    card.appendChild(body);

    return card;
  }

  /**
   * Generate star rating HTML
   * @param {number} rating - Rating value (0-5)
   * @param {string} size - 'large' or 'small'
   */
  generateStars(rating, size = 'large') {
    const starSize = size === 'large' ? 22 : 14;
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    let starsHTML = '';

    // Full stars
    for (let i = 0; i < fullStars; i++) {
      starsHTML += this.createStarSVG(starSize, 'full');
    }

    // Half star
    if (hasHalfStar) {
      starsHTML += this.createStarSVG(starSize, 'half');
    }

    // Empty stars
    for (let i = 0; i < emptyStars; i++) {
      starsHTML += this.createStarSVG(starSize, 'empty');
    }

    return starsHTML;
  }

  /**
   * Create star SVG with stroke (bordure) as per Figma design
   */
  createStarSVG(size, type) {
    const className = size === 22 ? 'judge-reviews__star' : 'judge-reviews__card-star';
    const strokeWidth = size === 22 ? '1.2' : '1';

    if (type === 'full') {
      return `
        <div class="${className}">
          <svg width="${size}" height="${size}" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8 0.375L10.1564 5.68237L15.875 6.13656L11.6094 9.87513L12.8844 15.4891L8 12.625L3.11562 15.4891L4.39062 9.87513L0.125 6.13656L5.84362 5.68237L8 0.375Z" fill="#352045" stroke="#352045" stroke-width="${strokeWidth}" stroke-linejoin="miter"/>
          </svg>
        </div>
      `;
    } else if (type === 'half') {
      return `
        <div class="${className}">
          <svg width="${size}" height="${size}" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="half-${size}">
                <stop offset="50%" stop-color="#352045"/>
                <stop offset="50%" stop-color="transparent"/>
              </linearGradient>
            </defs>
            <path d="M8 0.375L10.1564 5.68237L15.875 6.13656L11.6094 9.87513L12.8844 15.4891L8 12.625L3.11562 15.4891L4.39062 9.87513L0.125 6.13656L5.84362 5.68237L8 0.375Z" fill="url(#half-${size})" stroke="#352045" stroke-width="${strokeWidth}" stroke-linejoin="miter"/>
          </svg>
        </div>
      `;
    } else {
      return `
        <div class="${className}">
          <svg width="${size}" height="${size}" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8 0.375L10.1564 5.68237L15.875 6.13656L11.6094 9.87513L12.8844 15.4891L8 12.625L3.11562 15.4891L4.39062 9.87513L0.125 6.13656L5.84362 5.68237L8 0.375Z" fill="transparent" stroke="#352045" stroke-width="${strokeWidth}" stroke-linejoin="miter"/>
          </svg>
        </div>
      `;
    }
  }

  /**
   * Get initials from name
   */
  getInitials(name) {
    if (!name) return 'AN';

    const parts = name.trim().split(' ');
    if (parts.length === 1) {
      return parts[0].substring(0, 2).toUpperCase();
    }

    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  /**
   * Format date to DD/MM/YYYY
   */
  formatDate(dateString) {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  /**
   * Initialize Swiper carousel
   */
  initSwiper() {
    if (!this.carousel || typeof Swiper === 'undefined') {
      console.error('Swiper is not loaded or carousel element not found');
      return;
    }

    const isMobile = window.innerWidth < 989;

    this.swiper = new Swiper(this.carousel, {
      slidesPerView: 'auto',
      spaceBetween: isMobile ? 12 : 18,
      loop: false,
      grabCursor: true,
      breakpoints: {
        640: {
          slidesPerView: 'auto',
          spaceBetween: 12,
        },
        989: {
          slidesPerView: 'auto',
          spaceBetween: 18,
        },
        1200: {
          slidesPerView: 'auto',
          spaceBetween: 18,
        }
      },
      navigation: {
        nextEl: this.section.querySelector('.judge-reviews__nav-button--next'),
        prevEl: this.section.querySelector('.judge-reviews__nav-button--prev'),
      },
      pagination: {
        el: this.section.querySelector('.judge-reviews__pagination'),
        clickable: true,
        bulletClass: 'swiper-pagination-bullet',
        bulletActiveClass: 'swiper-pagination-bullet-active',
      },
    });
  }

  /**
   * Hide loading state
   */
  hideLoading() {
    if (this.loading) {
      this.loading.style.display = 'none';
    }
  }

  /**
   * Show API error with helpful message
   */
  showApiError(errorMessage) {
    if (this.loading) {
      let helpText = '';

      if (errorMessage.includes('authenticate') || errorMessage.includes('Api Token')) {
        helpText = `
          <div style="text-align: center; padding: 20px;">
            <p style="margin-bottom: 12px; font-weight: 600;">⚠️ Erreur d'authentification Judge.me</p>
            <p style="margin-bottom: 12px; font-size: 14px;">Les clés API ne sont pas valides.</p>
            <p style="font-size: 12px; opacity: 0.8;">
              Pour obtenir vos clés API :<br>
              1. Connectez-vous à <a href="https://judge.me" target="_blank" style="text-decoration: underline;">Judge.me</a><br>
              2. Allez dans Settings → API<br>
              3. Copiez votre "API Token" et "Shop Domain"<br>
              4. Mettez à jour les paramètres de la section dans Shopify
            </p>
          </div>
        `;
      } else if (errorMessage.includes('permissions')) {
        helpText = '<p>Token API insuffisant. Utilisez un token privé avec les permissions nécessaires.</p>';
      } else {
        helpText = `<p>Erreur API: ${errorMessage}</p>`;
      }

      this.loading.innerHTML = helpText;
      this.loading.style.display = 'flex';
    }
  }

  /**
   * Show error message
   */
  showError() {
    if (this.loading) {
      this.loading.innerHTML = '<p>Impossible de charger les avis. Veuillez réessayer plus tard.</p>';
    }
  }

  /**
   * Show no reviews message
   */
  showNoReviews() {
    if (this.loading) {
      this.loading.innerHTML = '<p>Aucun avis disponible pour le moment.</p>';
    }
  }
}

// Initialize all Judge Reviews sections on page load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initJudgeReviews);
} else {
  initJudgeReviews();
}

function initJudgeReviews() {
  const sections = document.querySelectorAll('[data-section-type="w-judge-reviews"]');
  sections.forEach(section => {
    new JudgeReviews(section);
  });
}

// Re-initialize on Shopify section events (theme editor)
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('[data-section-type="w-judge-reviews"]');
    if (section) {
      new JudgeReviews(section);
    }
  });
}
